import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Input,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Popover,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { usePagination } from '@mui/material';
import { DisputesTable } from '../../../../components/Admin/Payments/Disputes/DisputesTable';
import { ReportsTable } from '../../../../components/Admin/Payments/Disputes/ReportsTable';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import './Disputes.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { SupportHelpContent } from '../SupportHelp/SupportHelp';

// Utility function to format text
const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const StatusArray = [
  { value: 'All', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'rejected', label: 'Rejected' },
];

const UserRoleArray = [
  { value: 'All', label: 'All' },
  { value: 'student', label: 'Learner' },
  { value: 'teacher', label: 'Instructor' },
];

const SessionTypeArray = [
  { value: 'All', label: 'All' },
  { value: 'one_on_one', label: 'One on One Session' },
  { value: 'group', label: 'Group Session' },
  { value: 'prime', label: 'Prime Session' },
];

const Disputes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFilter, setOpenFilter] = useState(false);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);

  // Pending (UI) filter states
  const [userRole, setUserRole] = useState('All');
  const [sessionType, setSessionType] = useState('All');
  const [status, setStatus] = useState('All');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [country, setCountry] = useState('All');
  const [city, setCity] = useState('All');

  // Applied filter states (used for API calls)
  const [appliedUserRole, setAppliedUserRole] = useState('All');
  const [appliedSessionType, setAppliedSessionType] = useState('All');
  const [appliedStatus, setAppliedStatus] = useState('All');
  const [appliedSubject, setAppliedSubject] = useState('All');
  const [appliedTags, setAppliedTags] = useState([]);
  const [appliedCountry, setAppliedCountry] = useState('All');
  const [appliedCity, setAppliedCity] = useState('All');

  const [disputesData, setDisputesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasFetchedDisputes, setHasFetchedDisputes] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Report-specific states
  const [reportsData, setReportsData] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsCurrentPage, setReportsCurrentPage] = useState(1);
  const [reportsPageSize, setReportsPageSize] = useState(10);
  const [reportsTotalCount, setReportsTotalCount] = useState(0);
  const [reportsTotalPages, setReportsTotalPages] = useState(0);

  // Filter data states
  const [subjectList, setSubjectList] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);

  // Subject pagination state for infinite scroll
  const [subjectPage, setSubjectPage] = useState(1);
  const [subjectPageSize] = useState(30);
  const [subjectTotalCount, setSubjectTotalCount] = useState(0);
  const [subjectHasMore, setSubjectHasMore] = useState(true);
  const [subjectLoadingMore, setSubjectLoadingMore] = useState(false);

  // Tag pagination state
  const [tagPage, setTagPage] = useState(1);
  const [tagPageSize] = useState(40);
  const [tagTotalCount, setTagTotalCount] = useState(0);
  const [tagHasMore, setTagHasMore] = useState(true);
  const [tagLoadingMore, setTagLoadingMore] = useState(false);

  // Track if filter data has been loaded
  const [filterDataLoaded, setFilterDataLoaded] = useState(false);
  const [filterDataLoading, setFilterDataLoading] = useState(false);

  // Tag dropdown state
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagAnchorEl, setTagAnchorEl] = useState(null);
  const tagDropdownRef = useRef(null);

  // Popover anchor element
  const [anchorEl, setAnchorEl] = useState(null);

  // Support & Help filter states
  const [supportStatus, setSupportStatus] = useState('all');
  const [supportCategory, setSupportCategory] = useState('all');
  const [appliedSupportStatus, setAppliedSupportStatus] = useState('all');
  const [appliedSupportCategory, setAppliedSupportCategory] = useState('all');

  // Create refs to access current values
  const subjectListRef = useRef([]);
  const tagListRef = useRef([]);

  // Prevent duplicate initial fetch (Strict Mode), and dedupe in-flight requests
  const didInitialFetchRef = useRef(false);
  const inFlightRef = useRef(false);
  const lastQueryKeyRef = useRef('');
  const abortControllerRef = useRef(null);

  // Report-specific refs for dedupe and abort
  const didInitialReportsFetchRef = useRef(false);
  const reportsInFlightRef = useRef(false);
  const reportsLastQueryKeyRef = useRef('');
  const reportsAbortControllerRef = useRef(null);

  // Update refs when state changes
  useEffect(() => {
    subjectListRef.current = subjectList;
  }, [subjectList]);
  useEffect(() => {
    tagListRef.current = tagList;
  }, [tagList]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (location?.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API Functions
  const fetchSubjects = async (page = 1, size = 30) => {
    try {
      const { data } = await axiosInstance.get(`/common/subjects?page=${page}&size=${size}`);
      return data || { results: [], count: 0, limit: size };
    } catch (error) {
      console.error('Error fetching subjects:', error);
      return { results: [], count: 0, limit: 0 };
    }
  };

  const fetchTags = async (subjectId, page = 1, size = 40) => {
    try {
      const { data } = await axiosInstance.get(
        `/common/${subjectId}/tags?page=${page}&page_size=${size}`
      );
      return data || { results: [], count: 0, limit: size };
    } catch (error) {
      console.error('Error fetching tags:', error);
      return { results: [], count: 0, limit: 0 };
    }
  };

  const fetchCountries = async () => {
    try {
      const url = '/auth/countries';
      const { data } = await axiosInstance.get(url);
      setCountryList(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountryList([]);
    }
  };

  const fetchCities = async countryId => {
    try {
      const { data } = await axiosInstance.get(`/auth/countries/${countryId}/cities`);
      setCityList(data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCityList([]);
    }
  };

  // Fetch all disputes data (deduped, abortable)
  const fetchAllDisputes = useCallback(
    async (page = 1, size = 20, filtersOverride = null) => {
      try {
        const effectiveFilters = filtersOverride || {
          searchTerm,
          status: appliedStatus,
          userRole: appliedUserRole,
          sessionType: appliedSessionType,
          selectedSubject: appliedSubject,
          selectedTags: appliedTags,
          country: appliedCountry,
          city: appliedCity,
        };

        const key = JSON.stringify({
          page,
          size,
          searchTerm: effectiveFilters.searchTerm,
          status: effectiveFilters.status,
          userRole: effectiveFilters.userRole,
          sessionType: effectiveFilters.sessionType,
          selectedSubject: effectiveFilters.selectedSubject,
          selectedTags: effectiveFilters.selectedTags,
          country: effectiveFilters.country,
          city: effectiveFilters.city,
        });
        if (inFlightRef.current && lastQueryKeyRef.current === key) {
          return; // same request already in flight
        }
        inFlightRef.current = true;
        lastQueryKeyRef.current = key;

        setLoading(true);

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        // Build query parameters for filtering
        const queryParams = new URLSearchParams({
          page: page.toString(),
          page_size: size.toString(),
        });

        if (effectiveFilters.searchTerm) {
          queryParams.append('search_query', effectiveFilters.searchTerm);
        }
        if (effectiveFilters.status !== 'All') {
          queryParams.append('status', effectiveFilters.status.toLowerCase());
        }
        if (effectiveFilters.userRole !== 'All') {
          queryParams.append('user_role', effectiveFilters.userRole.toLowerCase());
        }
        if (effectiveFilters.sessionType !== 'All') {
          queryParams.append('session_type', effectiveFilters.sessionType.toLowerCase());
        }
        if (effectiveFilters.selectedSubject !== 'All') {
          queryParams.append('subject', effectiveFilters.selectedSubject.toLowerCase());
        }
        if (effectiveFilters.selectedTags && effectiveFilters.selectedTags.length > 0) {
          queryParams.append('tags', effectiveFilters.selectedTags.join(','));
        }
        if (effectiveFilters.country !== 'All') {
          queryParams.append('country', effectiveFilters.country.toLowerCase());
        }
        if (effectiveFilters.city !== 'All') {
          queryParams.append('city', effectiveFilters.city.toLowerCase());
        }

        const response = await axiosInstance.get(`/admin/disputes?${queryParams.toString()}`, {
          signal: abortControllerRef.current.signal,
        });
        const disputes = response.data.results || [];
        const count = response.data.count || 0;

        const formattedData = disputes.map(dispute => {
          const student =
            dispute.students && dispute.students.length > 0 ? dispute.students[0] : {};
          const teacher = dispute.booking?.teacher || {};

          const isTeacherDispute = (dispute.user_role || '').toLowerCase() === 'teacher';
          const displayUser = isTeacherDispute ? teacher : student;

          const nameParts = (displayUser.name || '').split(' ');

          return {
            id: dispute.id,
            first_name: nameParts[0] || '',
            last_name: nameParts[1] || '',
            userRole: isTeacherDispute ? 'Teacher' : 'Student',
            session_type: dispute.booking?.session_type || '',
            createdAt: dispute.created_at,
            reason: dispute.reason,
            amount: dispute.booking?.session_koins || 0,
            status: dispute.status ? dispute.status.toLowerCase() : '',
            userImage:
              displayUser.profile_image && displayUser.profile_image.startsWith('http')
                ? displayUser.profile_image
                : '',
            email: displayUser.email,
            phoneNo: displayUser.phone_no,
            teacher_name: teacher.name || '',
            teacher_email: teacher.email || '',
            subjects: dispute.subjects || [],
            tags: dispute.tags || [],
            teacher_type: dispute.teacher_type || '',
          };
        });

        // Sort data by date in descending order (newest first)
        const sortedData = formattedData.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setDisputesData(sortedData);
        setTotalCount(count);
        setTotalPages(Math.ceil(count / size));
        setCurrentPage(page);
        setHasFetchedDisputes(true);
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') return;
        console.error('Error fetching disputes:', error);
        setDisputesData([]);
        setTotalCount(0);
        setTotalPages(0);
        setHasFetchedDisputes(true);
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
    },
    [
      appliedStatus,
      appliedUserRole,
      appliedSessionType,
      appliedSubject,
      appliedTags,
      appliedCountry,
      appliedCity,
      searchTerm,
    ]
  );

  // Fetch all reports data (deduped, abortable)
  const fetchAllReports = useCallback(
    async (page = 1, size = 10, filtersOverride = null) => {
      try {
        const effectiveFilters = filtersOverride || {
          searchTerm,
          status: appliedStatus,
          userRole: appliedUserRole,
          sessionType: appliedSessionType,
          selectedSubject: appliedSubject,
          country: appliedCountry,
          city: appliedCity,
        };

        const key = JSON.stringify({
          page,
          size,
          searchTerm: effectiveFilters.searchTerm,
          status: effectiveFilters.status,
          userRole: effectiveFilters.userRole,
          sessionType: effectiveFilters.sessionType,
          selectedSubject: effectiveFilters.selectedSubject,
          country: effectiveFilters.country,
          city: effectiveFilters.city,
        });
        if (reportsInFlightRef.current && reportsLastQueryKeyRef.current === key) {
          return;
        }
        reportsInFlightRef.current = true;
        reportsLastQueryKeyRef.current = key;

        setReportsLoading(true);

        if (reportsAbortControllerRef.current) {
          reportsAbortControllerRef.current.abort();
        }
        reportsAbortControllerRef.current = new AbortController();

        const queryParams = new URLSearchParams({
          page: page.toString(),
          page_size: size.toString(),
        });

        if (effectiveFilters.searchTerm) {
          queryParams.append('search_query', effectiveFilters.searchTerm);
        }
        if (effectiveFilters.status !== 'All') {
          queryParams.append('status', effectiveFilters.status.toLowerCase());
        }
        if (effectiveFilters.userRole !== 'All') {
          queryParams.append('user_role', effectiveFilters.userRole.toLowerCase());
        }
        if (effectiveFilters.sessionType !== 'All') {
          queryParams.append('session_type', effectiveFilters.sessionType.toLowerCase());
        }
        if (effectiveFilters.selectedSubject !== 'All') {
          queryParams.append('subject', effectiveFilters.selectedSubject.toLowerCase());
        }
        if (effectiveFilters.country !== 'All') {
          queryParams.append('country', effectiveFilters.country.toLowerCase());
        }
        if (effectiveFilters.city !== 'All') {
          queryParams.append('city', effectiveFilters.city.toLowerCase());
        }

        const response = await axiosInstance.get(
          `/admin/report-sessions?${queryParams.toString()}`,
          {
            signal: reportsAbortControllerRef.current.signal,
          }
        );
        const reports = response.data.results || [];
        const count = response.data.count || 0;

        const formattedData = reports.map(report => {
          const reporter = report.reporter || {};
          const teacher = report.booking?.teacher || {};

          const nameParts = (reporter.name || '').split(' ');

          return {
            id: report.id,
            first_name: nameParts[0] || '',
            last_name: nameParts.slice(1).join(' ') || '',
            userRole: report.user_role === 'teacher' ? 'Teacher' : 'Student',
            session_type: report.booking?.session_type || '',
            createdAt: report.created_at,
            reason: report.reason,
            amount: report.booking?.session_koins || 0,
            status: report.status ? report.status.toLowerCase() : '',
            userImage:
              reporter.profile_image && reporter.profile_image.startsWith('http')
                ? reporter.profile_image
                : '',
            email: reporter.email,
            phoneNo: reporter.phone_no,
            teacher_name: teacher.name || '',
            teacher_email: teacher.email || '',
            teacher_type: report.teacher_type || '',
          };
        });

        const sortedData = formattedData.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setReportsData(sortedData);
        setReportsTotalCount(count);
        setReportsTotalPages(Math.ceil(count / size));
        setReportsCurrentPage(page);
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') return;
        console.error('Error fetching reports:', error);
        setReportsData([]);
        setReportsTotalCount(0);
        setReportsTotalPages(0);
      } finally {
        reportsInFlightRef.current = false;
        setReportsLoading(false);
      }
    },
    [
      appliedStatus,
      appliedUserRole,
      appliedSessionType,
      appliedSubject,
      appliedCountry,
      appliedCity,
      searchTerm,
    ]
  );

  // Initialize data on component mount - only run once
  useEffect(() => {
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;
    if (activeTab === 0) {
      fetchAllDisputes(1, pageSize);
    } else if (activeTab === 2) {
      if (!didInitialReportsFetchRef.current) {
        didInitialReportsFetchRef.current = true;
        fetchAllReports(1, reportsPageSize);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 0 && (!hasFetchedDisputes || disputesData.length === 0)) {
      setLoading(true);
      setHasFetchedDisputes(false);
      fetchAllDisputes(1, pageSize);
    } else if (activeTab === 2 && reportsData.length === 0) {
      setReportsLoading(true);
      fetchAllReports(1, reportsPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Pagination handlers (use applied filters)
  const handlePageChange = (event, newPage) => {
    if (activeTab === 0) {
      fetchAllDisputes(newPage, pageSize);
    } else if (activeTab === 2) {
      fetchAllReports(newPage, reportsPageSize);
    }
  };

  const handlePageSizeChange = event => {
    const newPageSize = event.target.value;
    if (activeTab === 0) {
      setPageSize(newPageSize);
      fetchAllDisputes(1, newPageSize);
    } else if (activeTab === 2) {
      setReportsPageSize(newPageSize);
      fetchAllReports(1, newPageSize);
    }
  };

  // Pagination items for UI - disputes
  const { items } = usePagination({
    count: activeTab === 0 ? totalPages : reportsTotalPages,
    page: activeTab === 0 ? currentPage : reportsCurrentPage,
    onChange: handlePageChange,
  });

  // Fetch cities when country changes (only while filter is open)
  useEffect(() => {
    if (!openFilter) return;
    if (country !== 'All') {
      const countryObj = countryList.find(c => c.name === country);
      if (countryObj) {
        fetchCities(countryObj.id);
      }
    } else {
      setCityList([]);
      setCity('All');
    }
  }, [openFilter, country, countryList]);

  // Fetch tags when subject changes (only while filter is open)
  useEffect(() => {
    if (!openFilter) return;
    if (selectedSubject !== 'All') {
      const subject = subjectList.find(s => s.title === selectedSubject);
      if (subject) {
        // Reset tag pagination state
        setTagPage(1);
        setTagHasMore(true);
        setTagLoadingMore(false);

        // Initial tag load
        const loadInitialTags = async () => {
          const { results: initialTags, count } = await fetchTags(subject.id, 1, tagPageSize);
          setTagList(initialTags || []);
          setTagTotalCount(count || 0);
          setTagHasMore((initialTags?.length || 0) < (count || 0));
        };
        loadInitialTags();
      }
    } else {
      setTagList([]);
      setSelectedTags([]);
      setTagPage(1);
      setTagHasMore(true);
      setTagTotalCount(0);
    }
  }, [openFilter, selectedSubject, subjectList, tagPageSize]);

  // Add click outside handler for tag dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        tagAnchorEl &&
        !tagAnchorEl.contains(event.target) &&
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target)
      ) {
        setTagDropdownOpen(false);
        setTagAnchorEl(null);
      }
    };

    if (tagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tagDropdownOpen, tagAnchorEl]);

  // Load filter data when filter is opened (only once)
  const loadFilterData = useCallback(async () => {
    if (filterDataLoaded) return; // Don't load again if already loaded

    setFilterDataLoading(true);
    try {
      // Initial subjects load (page 1)
      const { results: initialSubjects, count } = await fetchSubjects(1, subjectPageSize);
      await fetchCountries();

      setSubjectList(initialSubjects || []);
      setSubjectPage(1);
      setSubjectTotalCount(count || 0);
      setSubjectHasMore((initialSubjects?.length || 0) < (count || 0));
      setFilterDataLoaded(true);
    } catch (error) {
      console.error('Error loading filter data:', error);
    } finally {
      setFilterDataLoading(false);
    }
  }, [filterDataLoaded, subjectPageSize]);

  // Load more subjects when user scrolls to bottom of the subject dropdown
  const loadMoreSubjects = useCallback(async () => {
    if (subjectLoadingMore || !subjectHasMore) return;
    setSubjectLoadingMore(true);
    try {
      const nextPage = subjectPage + 1;
      const { results: nextSubjects, count } = await fetchSubjects(nextPage, subjectPageSize);
      const total = count || subjectTotalCount;
      setSubjectTotalCount(total);
      setSubjectList(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const merged = [...prev];
        (nextSubjects || []).forEach(s => {
          if (!existingIds.has(s.id)) merged.push(s);
        });
        // Update hasMore based on merged length vs total
        setSubjectHasMore(merged.length < total);
        return merged;
      });
      setSubjectPage(nextPage);
    } catch (e) {
      console.error('Error loading more subjects:', e);
    } finally {
      setSubjectLoadingMore(false);
    }
  }, [subjectLoadingMore, subjectHasMore, subjectPage, subjectPageSize, subjectTotalCount]);

  // Handle scroll for subject Select menu list
  const handleSubjectMenuScroll = event => {
    const list = event.currentTarget;
    if (!list) return;
    const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
    if (nearBottom) {
      loadMoreSubjects();
    }
  };

  // Load more tags when user scrolls to bottom of the tag dropdown
  // Note: Currently not used in the UI, but kept for future tag filter implementation
  // eslint-disable-next-line no-unused-vars
  const loadMoreTags = useCallback(async () => {
    if (tagLoadingMore || !tagHasMore || selectedSubject === 'All') return;

    const subject = subjectList.find(s => s.title === selectedSubject);
    if (!subject) return;

    setTagLoadingMore(true);
    try {
      const nextPage = tagPage + 1;
      const { results: nextTags, count } = await fetchTags(subject.id, nextPage, tagPageSize);
      const total = count || tagTotalCount;
      setTagTotalCount(total);

      setTagList(prev => {
        const existingIds = new Set(prev.map(t => t.id));
        const merged = [...prev];
        (nextTags || []).forEach(t => {
          if (!existingIds.has(t.id)) merged.push(t);
        });
        // Update hasMore based on merged length vs total
        setTagHasMore(merged.length < total);
        return merged;
      });
      setTagPage(nextPage);
    } catch (e) {
      console.error('Error loading more tags:', e);
    } finally {
      setTagLoadingMore(false);
    }
  }, [
    tagLoadingMore,
    tagHasMore,
    selectedSubject,
    subjectList,
    tagPage,
    tagPageSize,
    tagTotalCount,
  ]);

  // Event Handlers
  const handleChangeSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleClickToOpenFilter = async event => {
    setAnchorEl(event.currentTarget);
    setOpenFilter(true);

    // Load filter data when filter is opened (only for Dispute/Report tabs)
    if (activeTab === 0 || activeTab === 2) {
      await loadFilterData();
    } else if (activeTab === 1) {
      // For Support & Help, set UI state to match applied state
      setSupportStatus(appliedSupportStatus);
      setSupportCategory(appliedSupportCategory);
    }
  };

  const handleApplyFilter = () => {
    setOpenFilter(false);
    setAnchorEl(null);

    // Build effective filters from current pending values
    const nextFilters = {
      searchTerm,
      status,
      userRole,
      sessionType,
      selectedSubject,
      selectedTags,
      country,
      city,
    };

    // Apply current pending filter values
    setAppliedUserRole(userRole);
    setAppliedSessionType(sessionType);
    setAppliedStatus(status);
    setAppliedSubject(selectedSubject);
    setAppliedTags(selectedTags);
    setAppliedCountry(country);
    setAppliedCity(city);

    // Reset to first page and fetch with these filters immediately
    if (activeTab === 0) {
      fetchAllDisputes(1, pageSize, nextFilters);
    } else if (activeTab === 2) {
      const reportsFilters = { ...nextFilters };
      delete reportsFilters.selectedTags;
      fetchAllReports(1, reportsPageSize, reportsFilters);
    }
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setOpenFilter(false);
  };

  const handleClearAll = () => {
    if (activeTab === 0 || activeTab === 2) {
      setUserRole('All');
      setSessionType('All');
      setStatus('All');
      setSelectedSubject('All');
      setSelectedTags([]);
      setCountry('All');
      setCity('All');
    } else if (activeTab === 1) {
      setSupportStatus('all');
      setSupportCategory('all');
    }
  };

  const handleSupportApplyFilter = () => {
    setAppliedSupportStatus(supportStatus);
    setAppliedSupportCategory(supportCategory);
    setOpenFilter(false);
    setAnchorEl(null);
    // SupportHelpContent will refetch with new filters via useEffect
  };

  // Search submit on Enter key
  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      if (activeTab === 0) {
        fetchAllDisputes(1, pageSize);
      } else if (activeTab === 2) {
        fetchAllReports(1, reportsPageSize);
      }
    }
  };

  // Add a new search submit function
  const handleSearchSubmit = () => {
    if (activeTab === 0) {
      fetchAllDisputes(1, pageSize);
    } else if (activeTab === 1) {
      // Support & Help search is handled by SupportHelpContent component
      // Just trigger a re-render by updating search term
    } else if (activeTab === 2) {
      fetchAllReports(1, reportsPageSize);
    }
  };

  return (
    <Layout title="Klass Online | Support & Dispute">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] relative bg-[#F9F9F9] rounded-lg mr-[10px] flex flex-col overflow-y-auto">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Typography className="request-title">Support & Dispute</Typography>
              {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                <Box className="flex items-center gap-4 w-[40%]">
                  <Input
                    type="search"
                    placeholder={
                      activeTab === 1
                        ? 'Search by name, email, or description...'
                        : 'Search by user name, phone number, or email'
                    }
                    onChange={handleChangeSearch}
                    value={searchTerm}
                    startAdornment={
                      <SearchIcon
                        className="search-icon-css"
                        onClick={handleSearchSubmit}
                        style={{ cursor: 'pointer' }}
                      />
                    }
                    onKeyDown={handleSearchKeyDown}
                    sx={{
                      width: '100%',
                      height: '40px',
                      background: '#FFF',
                      borderRadius: '56px',
                      padding: '8px 10px',
                      border: '1px solid var(--Stroke, #D5DEE3)',
                      '&:before, &:after': {
                        borderBottom: 'none !important',
                      },
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none !important',
                      },
                      '& .MuiInputBase-input': {
                        padding: '3px 5px 3px 5px',
                      },
                    }}
                  />
                  {openFilter ? (
                    <img
                      src={ColoredFilterIcon}
                      alt="filter"
                      onClick={handleCloseFilter}
                      style={{ width: 25, height: 25, cursor: 'pointer' }}
                    />
                  ) : (
                    <img
                      src={FilterIcon}
                      alt="filter"
                      onClick={handleClickToOpenFilter}
                      style={{ width: 25, height: 25, cursor: 'pointer' }}
                    />
                  )}
                </Box>
              )}
            </Box>

            <Box>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 36 }}>
                <Tab label="Dispute" sx={{ textTransform: 'none', minHeight: 36 }} />
                <Tab label="Support & Help" sx={{ textTransform: 'none', minHeight: 36 }} />
                <Tab label="Report" sx={{ textTransform: 'none', minHeight: 36 }} />
              </Tabs>
            </Box>

            {(activeTab === 0 || activeTab === 2) && (
              <Popover
                open={openFilter}
                anchorEl={anchorEl}
                onClose={handleCloseFilter}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  className: 'filter-modal-container',
                }}
              >
                {/* Header */}
                <Box className="filter-modal-header">
                  <Typography className="filter-modal-title">Filters</Typography>
                  {filterDataLoading && (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#666',
                        fontStyle: 'italic',
                      }}
                    >
                      Loading filter options...
                    </Typography>
                  )}
                </Box>

                {/* Scrollable Content */}
                <Box className="filter-modal-content">
                  {/* Grid for filter items */}
                  <Box className="filter-grid">
                    {/* User Role Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">User Role</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={userRole}
                          onChange={e => setUserRole(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {UserRoleArray.map(role => (
                            <MenuItem
                              key={role.value}
                              value={role.value}
                              className="dropdown-option"
                            >
                              {role.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Session Type Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Session Type</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={sessionType}
                          onChange={e => setSessionType(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {SessionTypeArray.map(type => (
                            <MenuItem
                              key={type.value}
                              value={type.value}
                              className="dropdown-option"
                            >
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Status Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Status</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={status}
                          onChange={e => setStatus(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {StatusArray.map(statusItem => (
                            <MenuItem
                              key={statusItem.value}
                              value={statusItem.value}
                              className="dropdown-option"
                            >
                              {statusItem.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Subject Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Subject</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={selectedSubject}
                          onChange={e => setSelectedSubject(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          disabled={filterDataLoading}
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                                maxHeight: 200,
                              },
                            },
                            MenuListProps: {
                              onScroll: handleSubjectMenuScroll,
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {(subjectList || []).map(subject => (
                            <MenuItem
                              key={subject.id}
                              value={subject.title}
                              className="dropdown-option"
                            >
                              {formatText(subject.title)}
                            </MenuItem>
                          ))}
                          {subjectLoadingMore && (
                            <MenuItem disabled className="dropdown-option">
                              Loading...
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Country Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Country</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={country}
                          onChange={e => setCountry(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          disabled={filterDataLoading}
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {countryList.map(countryItem => (
                            <MenuItem
                              key={countryItem.id}
                              value={countryItem.name}
                              className="dropdown-option"
                            >
                              {countryItem.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* City Filter - Only show when country is selected */}
                    {country !== 'All' && (
                      <Box className="filter-item">
                        <label className="filter-label">City</label>
                        <FormControl sx={{ width: '100%' }}>
                          <Select
                            value={city}
                            onChange={e => setCity(e.target.value)}
                            displayEmpty
                            className="filter-input"
                            IconComponent={KeyboardArrowDownIcon}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                  zIndex: 1500,
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                              },
                            }}
                          >
                            <MenuItem value="All" className="dropdown-option">
                              All
                            </MenuItem>
                            {cityList.map(cityItem => (
                              <MenuItem
                                key={cityItem.id}
                                value={cityItem.name}
                                className="dropdown-option"
                              >
                                {cityItem.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Buttons (Sticky Footer) */}
                <Box className="filter-buttons-container">
                  <Button
                    variant="outlined"
                    onClick={handleClearAll}
                    className="filter-clear-button"
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilter}
                    className="filter-apply-button"
                  >
                    Apply
                  </Button>
                </Box>
              </Popover>
            )}

            {activeTab === 1 && (
              <Popover
                open={openFilter}
                anchorEl={anchorEl}
                onClose={handleCloseFilter}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  className: 'filter-modal-container',
                }}
              >
                <Box className="filter-modal-header">
                  <Typography className="filter-modal-title">Filters</Typography>
                </Box>
                <Box className="filter-modal-content">
                  <Box className="filter-grid">
                    <Box className="filter-item">
                      <label className="filter-label">Status</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={supportStatus}
                          onChange={e => setSupportStatus(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="all" className="dropdown-option">
                            All Status
                          </MenuItem>
                          <MenuItem value="pending" className="dropdown-option">
                            Pending
                          </MenuItem>
                          <MenuItem value="resolved" className="dropdown-option">
                            Resolved
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box className="filter-item">
                      <label className="filter-label">Category</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={supportCategory}
                          onChange={e => setSupportCategory(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="all" className="dropdown-option">
                            All Categories
                          </MenuItem>
                          <MenuItem value="not_receiving_notifications" className="dropdown-option">
                            Not Receiving Notifications
                          </MenuItem>
                          <MenuItem value="app_not_loading" className="dropdown-option">
                            App Not Loading
                          </MenuItem>
                          <MenuItem value="session_booking_not_working" className="dropdown-option">
                            Session Booking Not Working
                          </MenuItem>
                          <MenuItem value="others" className="dropdown-option">
                            Others
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
                <Box className="filter-buttons-container">
                  <Button
                    variant="outlined"
                    onClick={handleClearAll}
                    className="filter-clear-button"
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSupportApplyFilter}
                    className="filter-apply-button"
                  >
                    Apply
                  </Button>
                </Box>
              </Popover>
            )}

            {activeTab === 0 && (
              <Box className="flex flex-col flex-grow h-full overflow-hidden">
                <Box className="request-table-container flex-grow h-full flex flex-col overflow-hidden">
                  <DisputesTable
                    data={disputesData}
                    loading={loading || !hasFetchedDisputes}
                    startIndex={(currentPage - 1) * pageSize}
                  />

                  {/* Pagination UI */}
                  <Box
                    className="disputes-pagination-container"
                    sx={{ marginTop: 0, paddingTop: 0 }}
                  >
                    <Box className="disputes-pagination-info">
                      <Typography className="disputes-pagination-text">Showing</Typography>
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className="disputes-pagination-select"
                        sx={{
                          height: '30px',
                          minWidth: '20px',
                          '& .MuiSelect-select': {
                            padding: '6px 4px 4px 8px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={30}>30</MenuItem>
                      </Select>
                      <Typography className="disputes-pagination-text">
                        of {totalCount} disputes
                      </Typography>
                    </Box>
                    <nav>
                      <ul className="disputes-pagination-nav">
                        {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                          let children = null;

                          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                            children = <span className="disputes-pagination-ellipsis">…</span>;
                          } else if (type === 'page') {
                            children = (
                              <button
                                type="button"
                                className={`disputes-pagination-button ${selected ? 'selected' : ''}`}
                                {...item}
                              >
                                {itemPage}
                              </button>
                            );
                          } else {
                            children = (
                              <button
                                type="button"
                                className="disputes-pagination-button"
                                {...item}
                              >
                                {formatText(type)}
                              </button>
                            );
                          }
                          return <li key={index}>{children}</li>;
                        })}
                      </ul>
                    </nav>
                  </Box>
                </Box>
              </Box>
            )}
            {activeTab === 1 && (
              <SupportHelpContent
                searchTerm={searchTerm}
                appliedSearchTerm={searchTerm}
                appliedStatus={appliedSupportStatus}
                appliedCategory={appliedSupportCategory}
              />
            )}
            {activeTab === 2 && (
              <Box className="flex flex-col flex-grow h-full overflow-hidden">
                <Box className="request-table-container flex-grow h-full flex flex-col overflow-hidden">
                  <ReportsTable
                    data={reportsData}
                    loading={reportsLoading}
                    startIndex={(reportsCurrentPage - 1) * reportsPageSize}
                  />

                  <Box
                    className="disputes-pagination-container"
                    sx={{ marginTop: 0, paddingTop: 0 }}
                  >
                    <Box className="disputes-pagination-info">
                      <Typography className="disputes-pagination-text">Showing</Typography>
                      <Select
                        value={reportsPageSize}
                        onChange={handlePageSizeChange}
                        className="disputes-pagination-select"
                        sx={{
                          height: '30px',
                          minWidth: '20px',
                          '& .MuiSelect-select': {
                            padding: '6px 4px 4px 8px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={30}>30</MenuItem>
                      </Select>
                      <Typography className="disputes-pagination-text">
                        of {reportsTotalCount} reports
                      </Typography>
                    </Box>
                    <nav>
                      <ul className="disputes-pagination-nav">
                        {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                          let children = null;

                          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                            children = <span className="disputes-pagination-ellipsis">…</span>;
                          } else if (type === 'page') {
                            children = (
                              <button
                                type="button"
                                className={`disputes-pagination-button ${selected ? 'selected' : ''}`}
                                {...item}
                              >
                                {itemPage}
                              </button>
                            );
                          } else {
                            children = (
                              <button
                                type="button"
                                className="disputes-pagination-button"
                                {...item}
                              >
                                {formatText(type)}
                              </button>
                            );
                          }
                          return <li key={index}>{children}</li>;
                        })}
                      </ul>
                    </nav>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Disputes;
